/** All date logic runs in America/Los_Angeles (Las Vegas time).
 *  Workers execute in UTC; swing shift (1400–2200 Pacific) crosses
 *  the UTC date boundary at 5 PM, so we must always resolve the
 *  calendar date in Pacific time. */

const TZ = 'America/Los_Angeles';

/** Return today's date in Las Vegas as YYYY-MM-DD. */
export function vegasToday(): string {
  return toVegasDate(new Date());
}

/** Convert any Date object to a YYYY-MM-DD string in Las Vegas time. */
export function toVegasDate(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find(p => p.type === 'year')!.value;
  const m = parts.find(p => p.type === 'month')!.value;
  const day = parts.find(p => p.type === 'day')!.value;
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into its year/month/day numbers. */
export function parseDate(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { year: y, month: m, day: d };
}

/** Add N calendar days to an ISO date string. */
export function addDays(iso: string, n: number): string {
  const { year, month, day } = parseDate(iso);
  const d = new Date(year, month - 1, day + n);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Day of week name (short) for an ISO date. */
export function dayName(iso: string): string {
  const { year, month, day } = parseDate(iso);
  const d = new Date(year, month - 1, day);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}

/** Full weekday name for an ISO date. */
export function weekdayFull(iso: string): string {
  const { year, month, day } = parseDate(iso);
  const d = new Date(year, month - 1, day);
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
}

/** Format as "Wednesday, October 17, 2026". */
export function dateLong(iso: string): string {
  const { year, month, day } = parseDate(iso);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return `${weekdayFull(iso)}, ${months[month - 1]} ${day}, ${year}`;
}

/** Day of month from ISO string. */
export function dayOfMonth(iso: string): number {
  return parseDate(iso).day;
}

/** Difference in days between two ISO dates (b - a). */
export function diffDays(a: string, b: string): number {
  const pa = parseDate(a);
  const pb = parseDate(b);
  const da = new Date(pa.year, pa.month - 1, pa.day);
  const db = new Date(pb.year, pb.month - 1, pb.day);
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}
